import { useEffect, useState } from "react";
import { Input } from "@volca/ui/components/ui/input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";

interface SheetNameFieldProps {
  id: string;
  label: string;
  name: string;
  disabled: boolean;
  // Resolves true once the new name is saved.
  onCommit: (name: string) => Promise<boolean>;
}

// Saves when the field loses focus. The typed name stays on screen while the
// save runs and after it fails, so a failed rename can be retried.
export const SheetNameField = ({
  id,
  label,
  name,
  disabled,
  onCommit,
}: SheetNameFieldProps) => {
  const [value, setValue] = useState(name);
  const [failed, setFailed] = useState(false);
  const errorId = `${id}-error`;

  useEffect(() => {
    setValue(name);
    setFailed(false);
  }, [name]);

  return (
    <Stack className="w-full" align="start" gap={1}>
      <Input
        id={id}
        aria-label={label}
        aria-invalid={failed}
        aria-describedby={failed ? errorId : undefined}
        disabled={disabled}
        value={value}
        className={cn({ "border-danger-line": failed })}
        onChange={(event) => setValue(event.target.value)}
        onBlur={async () => {
          const next = value.trim();
          if (!next || next === name) {
            setValue(name);
            setFailed(false);
            return;
          }
          setFailed(!(await onCommit(next)));
        }}
      />
      {failed && (
        <Text id={errorId} variant="caption" tone="destructive">
          This name wasn&apos;t saved.
        </Text>
      )}
    </Stack>
  );
};
